import React, { useState } from 'react';
import { Modal, Table, Button, Space, message, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, KeyOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useCustomerAccounts } from '../graphql/queries/customerManagement';
import {
    useCreateCustomerAccount,
    useDeleteCustomerAccount,
    useResetCustomerAccountPassword,
    useSendCustomerAccountCredentials,
} from '../graphql/mutations/customerManagement';
import {
    CustomerAccountType,
    CustomerRoleCode,
    CUSTOMER_ROLE_ADMIN,
    CUSTOMER_ROLE_OPERATOR,
} from '../graphql/types/CustomerManagementTypes';
import { showError } from 'utils/exceptionUtils';
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';
import dayjs from 'dayjs';

interface CustomerAccountModalProps {
    visible: boolean;
    onClose: () => void;
    channelId: number;
    channelName?: string;
    itContactEmail?: string;
}

/** 每个角色独立的激活账号上限，与 customer-service AccountManagementAppServiceImpl.MAX_ACTIVE_ACCOUNTS 一致 */
const MAX_ACTIVE_ACCOUNTS_PER_ROLE = 3;

/** 每个角色分组对应的资源码：新增 / 删除 / 重置密码 / 发送邮件 */
const ROLE_RESOURCES: Record<CustomerRoleCode, { create: string; del: string; reset: string; send: string }> = {
    [CUSTOMER_ROLE_ADMIN]: {
        create: ResourceCode.crm.CRM_CHANNEL_ADMIN_ACCOUNT_CREATE,
        del: ResourceCode.crm.CRM_CHANNEL_ADMIN_ACCOUNT_DELETE,
        reset: ResourceCode.crm.CRM_CHANNEL_ADMIN_ACCOUNT_RESET_PASSWORD,
        send: ResourceCode.crm.CRM_CHANNEL_ADMIN_ACCOUNT_SEND_EMAIL,
    },
    [CUSTOMER_ROLE_OPERATOR]: {
        create: ResourceCode.crm.CRM_CHANNEL_OPERATOR_ACCOUNT_CREATE,
        del: ResourceCode.crm.CRM_CHANNEL_OPERATOR_ACCOUNT_DELETE,
        reset: ResourceCode.crm.CRM_CHANNEL_OPERATOR_ACCOUNT_RESET_PASSWORD,
        send: ResourceCode.crm.CRM_CHANNEL_OPERATOR_ACCOUNT_SEND_EMAIL,
    },
};

/** 后端对无角色绑定的存量账号按 CUSTOMER_ADMIN 兜底，前端分组口径与之保持一致 */
const roleOf = (account: CustomerAccountType): CustomerRoleCode =>
    account.roleCode === CUSTOMER_ROLE_OPERATOR ? CUSTOMER_ROLE_OPERATOR : CUSTOMER_ROLE_ADMIN;

const CustomerAccountModal: React.FC<CustomerAccountModalProps> = ({
    visible,
    onClose,
    channelId,
    channelName,
    itContactEmail,
}) => {
    const { t } = useTranslation();
    const { data, loading, refetch } = useCustomerAccounts(visible ? channelId : null);
    const [createAccount, { loading: creating }] = useCreateCustomerAccount();
    const [deleteAccount, { loading: deleting }] = useDeleteCustomerAccount();
    const [resetPassword, { loading: resetting }] = useResetCustomerAccountPassword();
    const [sendCredentials, { loading: sending }] = useSendCustomerAccountCredentials();
    const [newPassword, setNewPassword] = useState<{ accountNumber: string; password: string } | null>(null);
    const [creatingRole, setCreatingRole] = useState<CustomerRoleCode | null>(null);

    const accounts = data?.customerAccounts || [];

    const handleCreate = async (roleCode: CustomerRoleCode) => {
        setCreatingRole(roleCode);
        try {
            const { data: result } = await createAccount({ variables: { channelId, roleCode } });
            if (result?.createCustomerAccount) {
                const created = result.createCustomerAccount;
                message.success(t('central.channels.crm.accountModal.accountCreated'));
                if (created.plainPassword) {
                    setNewPassword({ accountNumber: created.accountNumber, password: created.plainPassword });
                }
                refetch();
            }
        } catch (e) {
            showError(e);
        } finally {
            setCreatingRole(null);
        }
    };

    const handleDelete = (record: CustomerAccountType) => {
        Modal.confirm({
            title: t('central.channels.crm.accountModal.confirmDelete'),
            content: t('central.channels.crm.accountModal.confirmDeleteContent', { accountNumber: record.accountNumber }),
            okType: 'danger',
            onOk: async () => {
                try {
                    await deleteAccount({ variables: { accountId: record.accountId } });
                    message.success(t('central.channels.crm.accountModal.accountDeleted'));
                    refetch();
                } catch (e) {
                    showError(e);
                }
            },
        });
    };

    const handleResetPassword = (record: CustomerAccountType) => {
        Modal.confirm({
            title: t('central.channels.crm.accountModal.confirmReset'),
            content: t('central.channels.crm.accountModal.confirmResetContent', { accountNumber: record.accountNumber }),
            onOk: async () => {
                try {
                    await resetPassword({ variables: { accountId: record.accountId } });
                    message.success(t('central.channels.crm.accountModal.passwordReset'));
                    // PRD: 重置后二次弹窗询问是否发送
                    handlePostResetSend(record);
                    refetch();
                } catch (e) {
                    showError(e);
                }
            },
        });
    };

    const handlePostResetSend = (record: CustomerAccountType) => {
        Modal.confirm({
            title: t('central.channels.crm.accountModal.sendAfterReset'),
            content: t('central.channels.crm.accountModal.sendAfterResetContent', { accountNumber: record.accountNumber }),
            onOk: () => handleSendCredentials(record),
        });
    };

    const handleSendCredentials = (record: CustomerAccountType) => {
        const email = itContactEmail?.trim();
        if (!email) {
            message.error(t('central.channels.messages.itContactEmailRequired'));
            return;
        }
        Modal.confirm({
            title: t('central.channels.crm.accountModal.confirmSend'),
            content: t('central.channels.crm.accountModal.confirmSendContent', { accountNumber: record.accountNumber }),
            onOk: () => doSend(record.accountId, email),
        });
    };

    const doSend = async (accountId: number, email: string) => {
        try {
            await sendCredentials({ variables: { accountId, contactId: 0 } });
            message.success(t('central.channels.crm.accountModal.credentialsSent', { email }));
        } catch (e) {
            showError(e);
        }
    };

    const columns = [
        {
            title: t('central.channels.crm.accountModal.accountNumber'),
            dataIndex: 'accountNumber',
            key: 'accountNumber',
        },
        {
            title: t('central.channels.crm.accountModal.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                const colorMap: Record<string, string> = { ACTIVE: 'text-green-600', DISABLED: 'text-orange-500', DELETED: 'text-red-500' };
                return <span className={colorMap[status] || ''}>{status}</span>;
            },
        },
        {
            title: t('central.channels.crm.accountModal.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (val: string) => val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-',
        },
        {
            title: t('central.channels.crm.accountModal.actions'),
            key: 'actions',
            render: (_: unknown, record: CustomerAccountType) => {
                if (record.status !== 'ACTIVE') return null;
                const res = ROLE_RESOURCES[roleOf(record)];
                return (
                    <Space size="small">
                        {hasResource([res.del]) && (
                            <Button
                                type="link"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(record)}
                                loading={deleting}
                            >
                                {t('central.channels.crm.accountModal.delete')}
                            </Button>
                        )}
                        {hasResource([res.reset]) && (
                            <Button
                                type="link"
                                size="small"
                                icon={<KeyOutlined />}
                                onClick={() => handleResetPassword(record)}
                                loading={resetting}
                            >
                                {t('central.channels.crm.accountModal.resetPassword')}
                            </Button>
                        )}
                        {hasResource([res.send]) && (
                            <Button
                                type="link"
                                size="small"
                                icon={<SendOutlined />}
                                onClick={() => handleSendCredentials(record)}
                                loading={sending}
                            >
                                {t('central.channels.crm.accountModal.sendCredentials')}
                            </Button>
                        )}
                    </Space>
                );
            },
        },
    ];

    /**
     * 角色分组：标题 + 已用/上限额度标签 + 新增按钮 + 该组账号表格。
     * 额度按角色独立计算，达上限时标签标红、新增按钮置灰；服务端仍会兜底校验。
     */
    const renderGroup = (roleCode: CustomerRoleCode) => {
        const groupAccounts = accounts.filter((a) => roleOf(a) === roleCode);
        const activeCount = groupAccounts.filter((a) => a.status === 'ACTIVE').length;
        const full = activeCount >= MAX_ACTIVE_ACCOUNTS_PER_ROLE;
        const isAdmin = roleCode === CUSTOMER_ROLE_ADMIN;
        const canCreate = hasResource([ROLE_RESOURCES[roleCode].create]);
        return (
            <div key={roleCode} className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold">
                            {t(isAdmin ? 'central.channels.crm.accountModal.adminGroup' : 'central.channels.crm.accountModal.operatorGroup')}
                        </span>
                        <span className={full ? 'text-red-500 font-semibold' : 'text-gray-500'}>
                            {activeCount}/{MAX_ACTIVE_ACCOUNTS_PER_ROLE}
                        </span>
                    </div>
                    {canCreate && (
                        <Button
                            type={isAdmin ? 'primary' : 'default'}
                            icon={<PlusOutlined />}
                            onClick={() => handleCreate(roleCode)}
                            loading={creating && creatingRole === roleCode}
                            disabled={full || (creating && creatingRole !== roleCode)}
                            title={full ? (t('central.channels.crm.accountModal.maxAccountsReached') as string) : undefined}
                        >
                            {t(isAdmin ? 'central.channels.crm.accountModal.createAdminAccount' : 'central.channels.crm.accountModal.createOperatorAccount')}
                        </Button>
                    )}
                </div>
                <Table
                    dataSource={groupAccounts}
                    columns={columns}
                    rowKey="accountId"
                    loading={loading}
                    pagination={false}
                    size="small"
                />
            </div>
        );
    };

    return (
        <>
            <Modal
                title={
                    <div className="flex items-center">
                        <div className="w-1 h-6 bg-violet-600 mr-2" />
                        <span>{t('central.channels.crm.accountModal.title')}</span>
                    </div>
                }
                open={visible}
                onCancel={onClose}
                footer={null}
                width={860}
                destroyOnClose
            >
                <div className="mb-4 text-gray-600">
                    <span className="text-gray-500">{t('central.channels.crm.accountModal.customer')}: </span>
                    <span className="font-semibold">{channelName || channelId}</span>
                </div>
                {renderGroup(CUSTOMER_ROLE_ADMIN)}
                {renderGroup(CUSTOMER_ROLE_OPERATOR)}
            </Modal>

            {/* 新建/重置后显示密码弹窗 */}
            <Modal
                title={t('central.channels.crm.accountModal.newPassword')}
                open={!!newPassword}
                onCancel={() => setNewPassword(null)}
                footer={
                    <Button type="primary" onClick={() => setNewPassword(null)}>
                        {t('components.button.confirm')}
                    </Button>
                }
            >
                {newPassword && (
                    <div className="space-y-2">
                        <div>
                            <span className="text-gray-500">{t('central.channels.crm.accountModal.accountNumber')}: </span>
                            <span className="font-mono font-bold">{newPassword.accountNumber}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-gray-500">{t('central.channels.crm.accountModal.passwordLabel')}: </span>
                            <Typography.Text code copyable className="font-mono">
                                {newPassword.password}
                            </Typography.Text>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default CustomerAccountModal;
